import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { gamesApi } from '../../api/games';
import { useAuthStore } from '../../store/authStore';
import { Spinner } from '../../components/Spinner';
import type { GameSummary } from '../../types';

type KnownSlug = 'slot' | 'roulette' | 'blackjack';

interface KnownGame {
  slug: KnownSlug;
  name: string;
  path: string;
  emoji: string;
}

const KNOWN_GAMES: KnownGame[] = [
  { slug: 'slot', name: 'Slot Machine', path: '/games/slot', emoji: '🎰' },
  { slug: 'roulette', name: 'Roulette', path: '/games/roulette', emoji: '🎡' },
  { slug: 'blackjack', name: 'Blackjack', path: '/games/blackjack', emoji: '🃏' },
];

export function Dashboard() {
  const profile = useAuthStore((s) => s.profile);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['games'],
    queryFn: () => gamesApi.listGames(),
  });

  const enabledBySlug = new Map<string, GameSummary>(
    (data?.games ?? []).map((g) => [g.slug, g]),
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-slate-100">
          Welcome{profile?.displayName ? `, ${profile.displayName}` : ''} 👋
        </h1>
        <p className="text-sm text-slate-400">Pick a game and try your luck.</p>
      </header>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : isError ? (
        <p className="card text-sm text-rose-400">
          {error instanceof Error ? error.message : 'Failed to load games.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {KNOWN_GAMES.map((known) => {
            const game = enabledBySlug.get(known.slug);
            const enabled = Boolean(game);

            const cardBody = (
              <div
                className={`card flex h-full flex-col gap-2 transition-colors ${
                  enabled ? 'hover:border-gold' : 'opacity-60'
                }`}
              >
                <div className="text-4xl" aria-hidden>
                  {known.emoji}
                </div>
                <h2 className="text-lg font-semibold text-slate-100">
                  {game?.name ?? known.name}
                </h2>
                {enabled && game ? (
                  <p className="text-sm text-slate-400">
                    Bet {game.minBet.toLocaleString()} –{' '}
                    {game.maxBet.toLocaleString()} tokens
                  </p>
                ) : (
                  <p className="text-sm text-slate-500">Coming soon</p>
                )}
              </div>
            );

            return enabled ? (
              <Link key={known.slug} to={known.path} className="block">
                {cardBody}
              </Link>
            ) : (
              <div key={known.slug} aria-disabled>
                {cardBody}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
